// src/pages/Dashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Wrench,
  Users,
  UserCog,
  ShieldCheck,
  History,
  Layers,
  SlidersHorizontal,
  FileSearch,
  FileChartPie,
  ClipboardList,
  GitBranch,
  FolderGit2,
  PlusCircle,
  Link2,
  Settings,
  Eye,
  FileSpreadsheet,

  // ✅ grouped menu icons
  ChevronDown,
  Cloud,
  Server,
  Database,
  Table2,
  BarChart3,
  Search,
  ClipboardCheck,
  PenTool,
  TestTube2,
  FileCode2,
  Rocket,
  Cog,
  Sparkles,
  Puzzle,
} from "lucide-react";
import Faq from "./FAQ";
import logoutIcon from "../assets/logout.png";
import companyLogo from "../assets/jade.png";
import toolLogo from "../assets/sowlogo.png";
import "../styles/dashboard.css";
import "../styles/login.css";

/* ---------------- Icons map ---------------- */

const icons = {
  dashboard: <LayoutDashboard size={18} />,

  admin: <UserCog size={18} />,
  accelerators: <Sparkles size={18} />,
  rag: <Layers size={18} />,
  ragCloud: <Cloud size={18} />,
  ragOnPrem: <Server size={18} />,
  planning: <ClipboardList size={18} />,
  studio: <BarChart3 size={18} />,
  code: <GitBranch size={18} />,
  analysisDesign: <PenTool size={18} />,
  testing: <TestTube2 size={18} />,
  development: <FileCode2 size={18} />,
  deployment: <Rocket size={18} />,
  maintenance: <Cog size={18} />,
  compliance: <ClipboardCheck size={18} />,
  it: <ClipboardList size={18} />,

  users: <Users size={18} />,
  roles: <UserCog size={18} />,
  permissions: <ShieldCheck size={18} />,

  ragKb: <Layers size={18} />,
  ragJobs: <Wrench size={18} />,
  ragAnalytics: <History size={18} />,
  ragSearch: <Search size={18} />,

  sowRules: <SlidersHorizontal size={18} />,
  sowHistory: <History size={18} />,
  sowDraft: <FileText size={18} />,
  sowTemplate: <FileText size={18} />,
  sowViewer: <Eye size={18} />,

  datasource: <Database size={18} />,
  datasets: <Table2 size={18} />,
  csvDatasets: <FileSpreadsheet size={18} />,
  dashboards: <LayoutDashboard size={18} />,
  mydashboards: <FileChartPie size={18} />,

  codebaseRepo: <FolderGit2 size={18} />,
  codebaseAdd: <PlusCircle size={18} />,
  codebaseConnections: <Link2 size={18} />,
  codebaseConnAdd: <PlusCircle size={18} />,
  codebaseConnSettings: <Settings size={18} />,

  fileSearch: <FileSearch size={18} />,
  // ✅ NEW: MCP
  mcp: <Puzzle size={18} />,
};

/* ---------------- utils ---------------- */

function safeUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

function normalizePerms(source) {
  if (!Array.isArray(source)) return [];
  return source
    .map((p) => (typeof p === "string" ? p : p?.name))
    .filter(Boolean)
    .map((s) => s.toLowerCase());
}

function anyVisible(items = []) {
  return items.some(Boolean);
}

/* ---------------- Sidebar components ---------------- */

function SidebarItem({ to, icon, label, disabled = false, end = false }) {
  if (disabled) {
    return (
      <a
        href="#"
        className="nav-disabled"
        onClick={(e) => e.preventDefault()}
        title="Coming soon"
      >
        <span className="nav-ico">{icon}</span>
        <span className="nav-label">{label}</span>
        <span className="nav-soon">Soon</span>
      </a>
    );
  }

  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) => (isActive ? "active" : "")}
    >
      <span className="nav-ico">{icon}</span>
      <span className="nav-label">{label}</span>
    </NavLink>
  );
}

function SidebarGroup({
  id,
  icon,
  title,
  openMap,
  setOpenMap,
  defaultOpen = false,
  children,
}) {
  const open = openMap[id] ?? defaultOpen;
  const toggle = () => setOpenMap((m) => ({ ...m, [id]: !open }));

  return (
    <li className={`nav-group ${open ? "open" : ""}`}>
      <button type="button" className="nav-group-btn" onClick={toggle}>
        <span className="nav-ico">{icon}</span>
        <span className="nav-label">{title}</span>
        <span className={`nav-chevron ${open ? "open" : ""}`}>
          <ChevronDown size={16} />
        </span>
      </button>

      <ul className={`nav-sub ${open ? "open" : ""}`}>{children}</ul>
    </li>
  );
}

/* ---------------- Page ---------------- */
function FaqIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M1.5 4.2C1.5 3.04 2.44 2.1 3.6 2.1H10.4C11.56 2.1 12.5 3.04 12.5 4.2V8.1C12.5 9.26 11.56 10.2 10.4 10.2H7.9L5.4 12.3V10.2H3.6C2.44 10.2 1.5 9.26 1.5 8.1V4.2Z"
        stroke="currentColor"
        strokeWidth="0.5"
        strokeLinejoin="round"
      />
      <path
        d="M5.8 5.1C5.8 4.44 6.34 3.9 7 3.9C7.66 3.9 8.2 4.44 8.2 5.1C8.2 5.6 7.92 5.93 7.43 6.26C7.05 6.51 6.8 6.78 6.8 7.3"
        stroke="currentColor"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="7" cy="8.5" r="0.45" fill="currentColor" />
    </svg>
  );
}
export default function Dashboard() {
  const location = useLocation();

  const user = safeUser();
  const username = user.username || "User";
  const role =
    typeof user.role === "string" ? user.role : user.role?.name || "";

  const perms = normalizePerms(
    user?.role?.permissions || user?.permissions || [],
  );
  const show = (key) => perms.includes(String(key).toLowerCase());

  const canAdmin = anyVisible([
    show("Users"),
    show("Roles"),
    show("Permissions"),
  ]);
  const canRag = show("RAG");
  const canPlanning = show("SOW");
  const canStudio = show("Dashboard");
  const canCode = show("CODEBASE");
  const canIT = anyVisible([
    show("IT_REQUEST_CREATE"),
    show("IT_REQUEST_APPROVE"),
  ]);
  const canAccelerators = show("Accelerators");
  const canMcp = show("MCP");
  // Dummy sections (always visible)
  const canAnalysisDesign = true;
  const canTesting = show("XenonAI");
  const canDeployment = true;
  const canMaintenance = true;
  const canCompliance = true;

  const canADAI = show("ADAI");

  const logout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    window.location.href = "/login";
  };

  /* ---------------- Expand state ---------------- */

  const [openMap, setOpenMap] = useState({});

  useEffect(() => {
    const p = location.pathname || "/";
    const starts = (prefixes) => prefixes.some((x) => p.startsWith(x));

    const auto = {};
    auto.admin = starts(["/users", "/roles", "/permissions"]);
    auto.accelerators = starts(["/Accelerators", "/accelerators"]);

    auto.rag = starts(["/rag-"]);
    auto.rag_cloud = starts([
      "/rag-collections",
      "/rag-jobs",
      "/rag-analytics",
      "/rag-search",
    ]);
    auto.rag_onprem = starts(["/rag-onprem"]);

    auto.planning = starts([
      "/rules-config",
      "/sow-history-analysis",
      "/sowdraft",
      "/sowtemplate",
      "/sow-analysis-viewer",
    ]);

    auto.compliance = starts(["/compliance"]);
    auto.analysis_design = starts(["/requirements-studio", "/design-studio"]);
    auto.studio = starts([
      "/datasources",
      "/datasets",
      "/csv-datasets",
      "/dashboards",
      "/mydashboards",
    ]);
    auto.code = starts(["/codebase", "/scoringrules"]);
    auto.it = starts(["/it"]);

    setOpenMap((prev) => ({ ...auto, ...prev }));
  }, [location.pathname]);

  /* ---------------- Smaller nav + FIX alignment for multi-line headings ---------------- */

  const compactNavStyles = useMemo(
    () => `
    /* Smaller + cleaner sidebar text */
    .sidebar-nav a,
    .sidebar-nav .nav-group-btn,
    .sidebar-nav .nav-disabled{
      font-size: 12px !important;
      padding: 8px 16px !important;
      gap: 9px !important;
      margin: 2px 10px !important;

      /* ✅ FIX: force left alignment (prevents centered multi-line labels) */
      justify-content: flex-start !important;
      text-align: left !important;
      align-items: center !important;
    }

    /* ✅ FIX: label should be a normal block that can wrap left-aligned */
    .sidebar-nav .nav-label{
      display: block !important;
      flex: 1 1 auto !important;
      min-width: 0 !important;
      white-space: normal !important;
      line-height: 1.15 !important;
      text-align: left !important;
    }

    .sidebar-nav .nav-ico{
      width: 20px !important;
      flex: 0 0 20px !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
    }

    .sidebar-nav .nav-group-btn{
      width: calc(100% - 20px);
      margin-left: 10px !important;
      margin-right: 10px !important;
      border-radius: 999px;
      background: transparent;
      border: 0;
      color: inherit;
      cursor: pointer;
      display: flex;
    }
    .sidebar-nav .nav-group-btn:hover{
      background: rgba(37, 99, 235, 0.14);
      opacity: 1;
      transform: translateX(1px);
    }

    .sidebar-nav .nav-chevron{
      margin-left: auto;
      display: inline-flex;
      transition: transform .18s ease;
      opacity: .9;
      flex: 0 0 auto;
    }
    .sidebar-nav .nav-chevron.open{ transform: rotate(180deg); }

    .sidebar-nav .nav-sub{
      list-style: none;
      margin: 6px 0 8px 0;
      padding: 0 0 0 12px;
      display: none;
    }
    .sidebar-nav .nav-sub.open{ display: block; }

    /* Submenu items slightly smaller */
    .sidebar-nav .nav-sub a,
    .sidebar-nav .nav-sub .nav-disabled{
      font-size: 11.5px !important;
      padding: 7px 14px !important;
      margin: 2px 10px 2px 14px !important;
      border-radius: 999px;
      opacity: 0.9;

      /* keep left alignment in submenu too */
      justify-content: flex-start !important;
      text-align: left !important;
    }

    .sidebar-nav .nav-soon{
      font-size: 10px;
      padding: 2px 7px;
      border-radius: 999px;
      flex: 0 0 auto;
    }
  `,
    [],
  );

  return (
    <div className="sow-page dashboard-page">
      <style>{compactNavStyles}</style>

      {/* Header */}
      <header className="sow-login-header">
        <div className="sow-header-inner">
          <div className="sow-header-left">
            <img
              src={companyLogo}
              alt="Jade Global"
              className="sow-jade-logo"
            />
            <div className="sow-header-title-block">
              <div className="sow-header-title">Jade Orion</div>
              <div className="sow-header-subtitle">
                Unified Intelligent System for end-to-end software delivery
              </div>
            </div>
          </div>

          <div className="sow-header-right">
            <span className="sow-header-user">
              Welcome {username}
              {role ? `, ${role}` : ""}
            </span>
            <img
              src={logoutIcon}
              alt="Logout"
              aria-label="Logout"
              title="Logout"
              className="sow-header-logout"
              role="button"
              tabIndex={0}
              onClick={logout}
              onKeyDown={(e) =>
                (e.key === "Enter" || e.key === " ") && logout()
              }
            />
          </div>
        </div>
      </header>

      {/* Sidebar + Main */}
      <div className="dashboard-body">
        <aside className="sidebar">
          <nav className="sidebar-nav">
            <ul>
              {/* Dashboard */}
              <li>
                <SidebarItem
                  to="/"
                  end
                  icon={icons.dashboard}
                  label="Dashboard"
                />
              </li>

              {/* Admin */}
              {canAdmin && (
                <SidebarGroup
                  id="admin"
                  icon={icons.admin}
                  title="Admin"
                  openMap={openMap}
                  setOpenMap={setOpenMap}
                >
                  {show("Users") && (
                    <li>
                      <SidebarItem
                        to="/users"
                        icon={icons.users}
                        label="Users"
                      />
                    </li>
                  )}
                  {show("Roles") && (
                    <li>
                      <SidebarItem
                        to="/roles"
                        icon={icons.roles}
                        label="Roles"
                      />
                    </li>
                  )}
                  {show("Permissions") && (
                    <li>
                      <SidebarItem
                        to="/permissions"
                        icon={icons.permissions}
                        label="Permissions"
                      />
                    </li>
                  )}
                </SidebarGroup>
              )}

              {/* Planning */}
              {canPlanning && (
                <SidebarGroup
                  id="planning"
                  icon={icons.planning}
                  title="Planning"
                  openMap={openMap}
                  setOpenMap={setOpenMap}
                >
                  <li>
                    <SidebarItem
                      to="/rules-config"
                      icon={icons.sowRules}
                      label="SOW Rules"
                    />
                  </li>
                  <li>
                    <SidebarItem
                      to="/sow-history-analysis"
                      icon={icons.sowHistory}
                      label="SOW History Analysis"
                    />
                  </li>
                  <li>
                    <SidebarItem
                      to="/sowdraft"
                      icon={icons.sowDraft}
                      label="Draft SOW"
                    />
                  </li>
                  <li>
                    <SidebarItem
                      to="/sowtemplate"
                      icon={icons.sowTemplate}
                      label="SOW Template"
                    />
                  </li>
                  <li>
                    <SidebarItem
                      to="/sow-analysis-viewer"
                      icon={icons.sowViewer}
                      label="SOW Analysis"
                    />
                  </li>
                </SidebarGroup>
              )}

              {/* Analysis & Design */}
              {canAnalysisDesign && (
                <SidebarGroup
                  id="analysis_design"
                  icon={icons.analysisDesign}
                  title="Analysis & Design"
                  openMap={openMap}
                  setOpenMap={setOpenMap}
                >
                  <li>
                    <SidebarItem
                      to="/requirement-studio"
                      icon={icons.analysisDesign}
                      label="Requirement Studio"
                    />
                  </li>
                  <li>
                    <SidebarItem
                      to="/design-studio"
                      icon={icons.analysisDesign}
                      label="Design Studio"
                    />
                  </li>
                </SidebarGroup>
              )}

              {/* Code Quality & Analysis */}
              {canCode && (
                <SidebarGroup
                  id="code"
                  icon={icons.code}
                  title="Code Quality & Analysis"
                  openMap={openMap}
                  setOpenMap={setOpenMap}
                >
                  <li>
                    <SidebarItem
                      to="/codebase"
                      icon={icons.code}
                      label="Codebase Analysis"
                    />
                  </li>
                  <li>
                    <SidebarItem
                      to="/codebase"
                      end
                      icon={icons.codebaseRepo}
                      label="Repositories"
                    />
                  </li>
                  <li>
                    <SidebarItem
                      to="/codebase/repos/new"
                      icon={icons.codebaseAdd}
                      label="Add Repo"
                    />
                  </li>
                  <li>
                    <SidebarItem
                      to="/codebase/connections"
                      icon={icons.codebaseConnections}
                      label="Connections"
                    />
                  </li>
                  <li>
                    <SidebarItem
                      to="/codebase/connections/new"
                      icon={icons.codebaseConnAdd}
                      label="Add Connection"
                    />
                  </li>
                  <li>
                    <SidebarItem
                      to="/scoringrules"
                      icon={icons.sowRules}
                      label="Scoring Rules"
                    />
                  </li>
                </SidebarGroup>
              )}

              {/* Development */}
              {canADAI && (
                <SidebarGroup
                  id="development"
                  icon={icons.development}
                  title="Development"
                  openMap={openMap}
                  setOpenMap={setOpenMap}
                >
                  <li>
                    <SidebarItem
                      to="/development/adai"
                      icon={icons.development}
                      label="Advanced Documentation"
                    />
                  </li>
                </SidebarGroup>
              )}

              {/* Testing (dummy) */}
              {canTesting && (
                <SidebarGroup
                  id="testing"
                  icon={icons.testing}
                  title="Testing"
                  openMap={openMap}
                  setOpenMap={setOpenMap}
                >
                  <li>
                    <SidebarItem
                      to="/testing/xenon"
                      icon={icons.testing}
                      label="Xenon AI"
                    />
                  </li>
                </SidebarGroup>
              )}

              {/* Deployment */}
              {canDeployment && (
                <SidebarGroup
                  id="deployment"
                  icon={icons.deployment}
                  title="Deployment"
                  openMap={openMap}
                  setOpenMap={setOpenMap}
                >
                  <li>
                    <SidebarItem
                      to="/golive"
                      icon={icons.deployment}
                      label="GoLive"
                    />
                  </li>
                  <li>
                    <SidebarItem
                      to="/deployment/pipelines"
                      icon={icons.deployment}
                      label="Release Pipelines"
                      disabled
                    />
                  </li>
                  <li>
                    <SidebarItem
                      to="/deployment/health"
                      icon={icons.deployment}
                      label="Environment Health"
                      disabled
                    />
                  </li>
                </SidebarGroup>
              )}

              {/* Maintenance (dummy) */}
              {canMaintenance && (
                <SidebarGroup
                  id="maintenance"
                  icon={icons.maintenance}
                  title="Maintenance"
                  openMap={openMap}
                  setOpenMap={setOpenMap}
                >
                  <li>
                    <SidebarItem
                      to="/maintenance/incidents"
                      icon={<Wrench size={18} />}
                      label="Incidents"
                      disabled
                    />
                  </li>
                  <li>
                    <SidebarItem
                      to="/maintenance/ops"
                      icon={icons.maintenance}
                      label="Ops Dashboard"
                      disabled
                    />
                  </li>
                </SidebarGroup>
              )}

              {/* Audit & Compliance */}
              {canCompliance && (
                <SidebarGroup
                  id="compliance"
                  icon={icons.compliance}
                  title="Audit & Compliance"
                  openMap={openMap}
                  setOpenMap={setOpenMap}
                >
                  <li>
                    <SidebarItem
                      to="/compliance"
                      icon={<ClipboardCheck size={18} />}
                      label="IDP"
                    />
                  </li>
                  <li>
                    <SidebarItem
                      to="/compliance/iso"
                      icon={<ShieldCheck size={18} />}
                      label="ISO Assistant"
                      disabled
                    />
                  </li>
                  <li>
                    <SidebarItem
                      to="/compliance/cmmi"
                      icon={<ClipboardCheck size={18} />}
                      label="CMMI Assistant"
                      disabled
                    />
                  </li>
                </SidebarGroup>
              )}

              {/* Accelerators */}
              {canAccelerators && (
                <SidebarGroup
                  id="accelerators"
                  icon={icons.accelerators}
                  title="Accelerators"
                  openMap={openMap}
                  setOpenMap={setOpenMap}
                >
                  <li>
                    <SidebarItem
                      to="/Accelerators"
                      icon={icons.accelerators}
                      label="Accelerator Gallery"
                    />
                  </li>
                </SidebarGroup>
              )}

              {/* RAG */}
              {canRag && (
                <SidebarGroup
                  id="rag"
                  icon={icons.rag}
                  title="RAG"
                  openMap={openMap}
                  setOpenMap={setOpenMap}
                >
                  <SidebarGroup
                    id="rag_cloud"
                    icon={icons.ragCloud}
                    title="RAG Cloud"
                    openMap={openMap}
                    setOpenMap={setOpenMap}
                    defaultOpen={true}
                  >
                    <li>
                      <SidebarItem
                        to="/rag-collections"
                        icon={icons.ragKb}
                        label="RAG KB"
                      />
                    </li>
                    <li>
                      <SidebarItem
                        to="/rag-jobs"
                        icon={icons.ragJobs}
                        label="RAG Jobs"
                      />
                    </li>
                    <li>
                      <SidebarItem
                        to="/rag-analytics"
                        icon={icons.ragAnalytics}
                        label="RAG Analytics"
                      />
                    </li>
                    <li>
                      <SidebarItem
                        to="/rag-search"
                        icon={icons.ragSearch}
                        label="RAG Search"
                      />
                    </li>
                  </SidebarGroup>

                  <SidebarGroup
                    id="rag_onprem"
                    icon={icons.ragOnPrem}
                    title="RAG On-Prem"
                    openMap={openMap}
                    setOpenMap={setOpenMap}
                  >
                    <li>
                      <SidebarItem
                        to="/rag-onprem/kb"
                        icon={icons.ragKb}
                        label="On-Prem KB"
                        disabled
                      />
                    </li>
                    <li>
                      <SidebarItem
                        to="/rag-onprem/jobs"
                        icon={icons.ragJobs}
                        label="On-Prem Jobs"
                        disabled
                      />
                    </li>
                    <li>
                      <SidebarItem
                        to="/rag-onprem/search"
                        icon={icons.ragSearch}
                        label="On-Prem Search"
                        disabled
                      />
                    </li>
                  </SidebarGroup>
                </SidebarGroup>
              )}

              {/* AI Dashboard Studio */}
              {canStudio && (
                <SidebarGroup
                  id="studio"
                  icon={icons.studio}
                  title="AI Dashboard Studio"
                  openMap={openMap}
                  setOpenMap={setOpenMap}
                >
                  <li>
                    <SidebarItem
                      to="/datasources"
                      icon={icons.datasource}
                      label="Data Sources"
                    />
                  </li>
                  <li>
                    <SidebarItem
                      to="/datasets"
                      icon={icons.datasets}
                      label="Datasets"
                    />
                  </li>
                  <li>
                    <SidebarItem
                      to="/csv-datasets"
                      icon={icons.csvDatasets}
                      label="CSV Datasets"
                    />
                  </li>
                  <li>
                    <SidebarItem
                      to="/dashboards"
                      icon={icons.dashboards}
                      label="Create Dashboard"
                    />
                  </li>
                  <li>
                    <SidebarItem
                      to="/mydashboards"
                      icon={icons.mydashboards}
                      label="My Dashboards"
                    />
                  </li>
                </SidebarGroup>
              )}

              {/* ✅ NEW: MCP MENU */}
              {canMcp && (
                <SidebarGroup
                  id="mcp"
                  icon={icons.mcp}
                  title="MCP"
                  openMap={openMap}
                  setOpenMap={setOpenMap}
                >
                  <li>
                    <SidebarItem
                      to="/mcp"
                      icon={<Server size={18} />}
                      label="MCP Hub"
                    />
                  </li>
                  <li>
                    <SidebarItem
                      to="/mcp/logs"
                      icon={<History size={18} />}
                      label="MCP Logs"
                      disabled
                    />
                  </li>
                </SidebarGroup>
              )}

              {/* IT */}
              {canIT && (
                <SidebarGroup
                  id="it"
                  icon={icons.it}
                  title="IT"
                  openMap={openMap}
                  setOpenMap={setOpenMap}
                >
                  {show("IT_REQUEST_CREATE") && (
                    <>
                      <li>
                        <SidebarItem
                          to="/itnew-request"
                          icon={icons.it}
                          label="Create IT Request"
                        />
                      </li>
                      <li>
                        <SidebarItem
                          to="/myitrequests"
                          icon={icons.it}
                          label="My IT Requests"
                        />
                      </li>
                      <li>
                        <SidebarItem
                          to="/itchatbot"
                          icon={icons.it}
                          label="IT Chatbot"
                        />
                      </li>
                    </>
                  )}
                  {show("IT_REQUEST_APPROVE") && (
                    <li>
                      <SidebarItem
                        to="/itapprovals"
                        icon={icons.it}
                        label="IT Approvals"
                      />
                    </li>
                  )}
                </SidebarGroup>
              )}
              <SidebarItem to="/faq" icon={<FaqIcon />} label="FAQs" />
            </ul>
          </nav>
        </aside>

        <main className="dashboard-main">
          <section className="content">
            <Outlet />
          </section>
        </main>
      </div>
    </div>
  );
}
